import { useEffect, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';

import { formatDate } from 'lib/helpers/dateUtils';

import { Alert } from 'components/shared/Alert/Alert';
import Loader from 'components/shared/Loader/Loader';

import {
  COMPLETED_STATUSES,
  CUSTOM_REVIEW_COMPONENTS,
  IMPORT_FROM_ORG_CODE
} from '../../lib/constants';
import PHASES_CONFIG from '../../lib/phasesConfig';
import Accordion from '../Accordion/Accordion';

import StageHeader from './components/StageHeader';
import useGetAllStagesOverview from './lib/useGetAllStagesOverview';

const STAGES_CONFIG = PHASES_CONFIG.map((phase) => ({
  code: phase.code,
  icon: phase.icon
}));

const StepperReview = (props) => {
  const navigate = useNavigate();
  const { practice } = useOutletContext();
  const hasOrganization = Boolean(practice?.organization?.id);

  const hardGoLiveDate = props.wizardState?.instance?.hard_go_live_date;
  const wizard_instance_id = props?.wizardState?.instance?.id;
  const practiceId = props?.wizardState?.instance?.instance_id;

  // Hide Import from Org only when the data_import__organization substep
  // was auto-completed by the backend (after "Mark complete" on the org step)
  // and the practice still has no organization.
  // This is instance-scoped: the substep status belongs to this specific instance.
  const shouldHideImportFromOrg = (() => {
    if (hasOrganization) return false;

    const allSubsteps = props.wizardState?.steps?.flatMap((step) => step.substeps ?? []) ?? [];
    const importFromOrgSubstep = allSubsteps.find((sub) => sub.code === IMPORT_FROM_ORG_CODE);

    return importFromOrgSubstep && COMPLETED_STATUSES.includes(importFromOrgSubstep.status);
  })();

  const { stagesData, isAnyStageLoading, invalidateQueryKeys, stagesConfig } =
    useGetAllStagesOverview(wizard_instance_id, practiceId);

  const [stageExpandState, setStageExpandState] = useState({});

  const handleExpandAll = (stageCode, open) => {
    setStageExpandState((prev) => ({
      ...prev,
      [stageCode]: { version: (prev[stageCode]?.version ?? 0) + 1, open }
    }));
  };

  const stageQueryKeyMap = stagesConfig.reduce((acc, stage, index) => {
    acc[stage.code] = invalidateQueryKeys[index];
    return acc;
  }, {});

  const filterStepSubsteps = (step) => {
    if (!shouldHideImportFromOrg || !step.substeps) return step;
    const filtered = step.substeps.filter((sub) => sub.code !== IMPORT_FROM_ORG_CODE);
    if (filtered.length === step.substeps.length) return step;
    return { ...step, substeps: filtered };
  };

  const renderStepAccordion = (step, stageCode) => {
    const CustomComponent = CUSTOM_REVIEW_COMPONENTS[step.code];
    const hasCustomComponent = !!CustomComponent;
    const stepInvalidateQueryKeys = [stageQueryKeyMap[stageCode]];
    const expandState = stageExpandState[stageCode];

    const accordionProps = {
      wizardInstanceId: wizard_instance_id,
      navigateToSubstep: props.navigateToSubstep,
      className: 'rounded-none border-0 border-b',
      key: `${step.code}-${expandState?.version ?? 0}`,
      data: step,
      stepTitle: step.name,
      invalidateQueryKeys: stepInvalidateQueryKeys,
      defaultOpen: expandState?.open ?? false
    };

    if (hasCustomComponent) {
      accordionProps.panelComponent = (
        <CustomComponent data={step} navigateToSubstep={props.navigateToSubstep} />
      );
      accordionProps.panelClassName = 'p-0';
    }

    return <Accordion {...accordionProps} />;
  };

  useEffect(() => {
    props.updateConfigs({
      goBackHandler: props.onPrev,
      actions: [
        {
          text: 'Go to practice dashboard',
          onClick: () => {
            props?.isAdmin
              ? navigate(`/admin/practices/${practiceId}`)
              : navigate(`/portal/dashboard`);
          }
        }
      ]
    });
  }, []);

  const renderStage = (stageConfig) => {
    const stageData = stagesData[stageConfig.code];
    const rawSteps = stageData?.data?.steps || [];
    const steps = rawSteps.map(filterStepSubsteps);
    const title = stageData?.data?.overview_group_name;

    return (
      <Accordion
        key={stageConfig.code}
        ButtonComponent={
          <StageHeader
            icon={stageConfig.icon}
            title={title}
            steps={steps}
            expandAll={stageExpandState[stageConfig.code]?.open ?? false}
            onExpandAll={(open) => handleExpandAll(stageConfig.code, open)}
          />
        }
        panelClassName="p-0 bg-neutral-50"
        panelComponent={steps.map((step) => renderStepAccordion(step, stageConfig.code))}
        invalidateQueryKeys={[stageQueryKeyMap[stageConfig.code]]}
      />
    );
  };

  if (isAnyStageLoading) {
    return (
      <div className="flex items-center justify-center pb-7 pt-10">
        <Loader />
      </div>
    );
  }

  return (
    <div className="pb-7">
      <div className="flex flex-col gap-4">
        {hardGoLiveDate ? (
          <Alert
            color="primary"
            variant="lighter"
            title={`Go-live date is set for: ${formatDate(hardGoLiveDate, null, 'MM/dd/yyyy')}`}
            width="full"
          />
        ) : (
          <Alert
            color="danger"
            variant="lighter"
            title="Go-live date is not set yet."
            width="full"
          />
        )}
        {STAGES_CONFIG.map(renderStage)}
      </div>
    </div>
  );
};

export default StepperReview;
